import { Injectable, effect, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TitleStrategy, type RouterStateSnapshot } from '@angular/router';

import { I18nService } from './i18n.service';
import { isTranslationKey } from './translations/en';

const APP_NAME = 'Celeri';

/**
 * Document titles, in the reader's language.
 *
 * Routes carry a translation key in `title` rather than finished text —
 * resolving it in the route config would freeze the tab title in whatever
 * language was active when the bundle loaded, and it would never follow a
 * language change. A route whose title is not a key is used as-is, so a
 * one-off literal still works.
 */
@Injectable({ providedIn: 'root' })
export class TranslatedTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly i18n = inject(I18nService);

  /** The current route's key, kept so the title can be rebuilt in place. */
  private current: string | undefined;

  constructor() {
    super();

    // The router only calls `updateTitle` on navigation, so without this the
    // tab would keep the language it was last navigated in.
    effect(() => {
      this.i18n.language();
      this.render();
    });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.current = this.buildTitle(snapshot);
    this.render();
  }

  private render(): void {
    const raw = this.current;

    if (raw === undefined) {
      this.title.setTitle(APP_NAME);

      return;
    }

    const text = isTranslationKey(raw) ? this.i18n.t(raw) : raw;

    this.title.setTitle(`${text} · ${APP_NAME}`);
  }
}
